import { z } from 'zod';
import { AddressSchema, ContactPointSchema, HumanNameSchema, IsoDate, IsoDateTime, LanguageCodeSchema } from './common.js';

export const PatientIdentifierSchema = z.object({
  type: z.enum(['MRN', 'NATIONAL_ID', 'IQAMA', 'PASSPORT', 'INSURANCE', 'OTHER']),
  value: z.string().min(1),
  issuer: z.string().optional(),
});
export type PatientIdentifier = z.infer<typeof PatientIdentifierSchema>;

export const EmergencyContactSchema = z.object({
  name: z.string().min(1),
  relationship: z.string().optional(),
  phone: z.string().min(1),
});
export type EmergencyContact = z.infer<typeof EmergencyContactSchema>;

export const ConsentSchema = z.object({
  consentId: z.string(),
  type: z.enum(['TREATMENT', 'DATA_PROCESSING', 'PORTAL_ACCESS', 'SMS_REMINDERS', 'RESEARCH']),
  status: z.enum(['GRANTED', 'REVOKED', 'EXPIRED']),
  grantedAt: IsoDateTime,
  revokedAt: IsoDateTime.optional(),
  recordedByUserId: z.string(),
  documentId: z.string().optional(),
});
export type Consent = z.infer<typeof ConsentSchema>;

export const PatientStatusSchema = z.enum(['ACTIVE', 'INACTIVE', 'DECEASED', 'MERGED']);

export const PatientSchema = z.object({
  tenantId: z.string().min(1),
  patientId: z.string().min(1),
  mrn: z.string().min(1),
  name: HumanNameSchema,
  birthDate: IsoDate,
  sex: z.enum(['male', 'female', 'other', 'unknown']),
  identifiers: z.array(PatientIdentifierSchema).default([]),
  contacts: z.array(ContactPointSchema).default([]),
  address: AddressSchema.optional(),
  emergencyContacts: z.array(EmergencyContactSchema).default([]),
  preferredLanguage: LanguageCodeSchema.optional(),
  nationality: z.string().optional(),
  status: PatientStatusSchema.default('ACTIVE'),
  /** Set when status is MERGED: the surviving record. */
  mergedIntoPatientId: z.string().optional(),
  consents: z.array(ConsentSchema).default([]),
  /** Flags such as VIP, interpreter needed. Free-form but tenant-configurable. */
  flags: z.array(z.string()).default([]),
  /** Lower-cased, diacritics-stripped "family given" used for prefix search. */
  searchName: z.string(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  version: z.number().int().default(1),
});
export type Patient = z.infer<typeof PatientSchema>;

/** The subset of patient fields safe to show in a header/indicator and in LLM context. */
export const PatientBannerSchema = PatientSchema.pick({
  patientId: true, mrn: true, name: true, birthDate: true, sex: true, status: true, flags: true, preferredLanguage: true,
});
export type PatientBanner = z.infer<typeof PatientBannerSchema>;

export const PatientRegistrationInputSchema = z.object({
  name: HumanNameSchema,
  birthDate: IsoDate,
  sex: z.enum(['male', 'female', 'other', 'unknown']),
  identifiers: z.array(PatientIdentifierSchema).optional(),
  contacts: z.array(ContactPointSchema).optional(),
  address: AddressSchema.optional(),
  emergencyContacts: z.array(EmergencyContactSchema).optional(),
  preferredLanguage: LanguageCodeSchema.optional(),
  nationality: z.string().optional(),
  consents: z.array(ConsentSchema.pick({ type: true, status: true })).optional(),
});
export type PatientRegistrationInput = z.infer<typeof PatientRegistrationInputSchema>;
